/**
 * Shared internals for the kit chart primitives.
 *
 * Recharts is imported ONLY inside this folder (kit-first rule); app code reaches
 * for `ModelingCurveChart` / `ModelingColumnChart` via the barrel and never sees
 * recharts. This file holds the pieces both charts share:
 *
 *  - `ChartFrame` — the SSR-safe wrapper. TanStack Start renders on the server,
 *    where recharts' `ResponsiveContainer` has no width to measure (it would
 *    render at 0×0 and then jump on hydration). `ChartFrame` reserves a
 *    fixed-height box and only mounts the chart AFTER the client mounts, so the
 *    server emits a quiet placeholder of the exact same height and there's no
 *    hydration mismatch and no layout shift. The `height` prop sets the reserved
 *    box; the chart fills it via `ResponsiveContainer height="100%"`.
 *
 *  - `useChartColors` — recharts paints SVG and CANNOT resolve CSS custom
 *    properties for `stroke`/`fill`, so we read the design-system tokens off
 *    `:root` with `getComputedStyle` on the client and re-read them when the
 *    color scheme flips (light/dark). The chart re-renders with resolved hexes,
 *    staying dark-mode-safe with no hardcoded colors in the components.
 *
 *  - small presentational helpers (`cx`, tooltip building blocks).
 */
import {
  useEffect,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useComputedColorScheme } from '@mantine/core';
import styles from './charts.module.css';

/** Coalesce CSS-module class lookups (typed `string | undefined`) to a string. */
export function cx(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * `true` only after the component has mounted on the client. Server render and
 * the first client render both return `false` (matching markup → no hydration
 * mismatch); a layout effect flips it to `true` right after mount.
 */
function subscribeNoop(): () => void {
  return () => {};
}
export function useMounted(): boolean {
  // useSyncExternalStore keeps server snapshot (false) and first client snapshot
  // (false) identical, then re-renders true post-mount — the canonical SSR gate.
  return useSyncExternalStore(
    subscribeNoop,
    () => true, // client
    () => false, // server
  );
}

/** The token names every chart resolves off `:root` (recharts needs real hexes). */
const CHART_TOKENS = {
  primary: '--color-primary', // observed line (ink teal)
  accent: '--color-accent', // extrapolated dashed line (brass)
  bandBg: '--tint-brand-bg', // confidence band fill (low-opacity teal)
  exposureBar: '--tint-neutral-bg', // faint exposure bars
  grid: '--border-subtle',
  axis: '--fg-tertiary',
  axisLabel: '--fg-secondary',
  reference: '--border-strong', // boundary / break-even markers
  surface: '--bg-surface',
  positive: '--color-success', // column charts: positive values
  negative: '--color-error', // column charts: negative values
  cumulative: '--color-primary', // cumulative overlay line
} as const;

export type ChartColors = Record<keyof typeof CHART_TOKENS, string>;

const FALLBACK_COLORS: ChartColors = {
  primary: '#234E5C',
  accent: '#C49A3A',
  bandBg: 'rgba(35,78,92,0.10)',
  exposureBar: 'rgba(40,38,30,0.06)',
  grid: 'rgba(40,38,30,0.05)',
  axis: '#726E62',
  axisLabel: '#3A372E',
  reference: 'rgba(40,38,30,0.17)',
  surface: '#FFFFFF',
  positive: '#2E9460',
  negative: '#C8463B',
  cumulative: '#234E5C',
};

function readChartColors(): ChartColors {
  if (typeof window === 'undefined') return FALLBACK_COLORS;
  const root = getComputedStyle(document.documentElement);
  const resolved = {} as ChartColors;
  (Object.keys(CHART_TOKENS) as (keyof typeof CHART_TOKENS)[]).forEach((key) => {
    const value = root.getPropertyValue(CHART_TOKENS[key]).trim();
    resolved[key] = value || FALLBACK_COLORS[key];
  });
  return resolved;
}

/**
 * Resolve the design-system chart tokens to concrete colors on the client,
 * re-reading whenever the Mantine color scheme flips so the chart restyles for
 * dark mode. Returns the token fallbacks during SSR / before mount.
 */
export function useChartColors(): ChartColors {
  // Recompute on color-scheme change (the CSS vars under :root swap values).
  const scheme = useComputedColorScheme('light');
  const [colors, setColors] = useState<ChartColors>(FALLBACK_COLORS);
  useEffect(() => {
    setColors(readChartColors());
  }, [scheme]);
  return colors;
}

/**
 * SSR-safe chart wrapper. Reserves a fixed-height box (CLS-safe) and renders the
 * `children` (the recharts tree) only after the client mounts; until then it
 * shows a quiet placeholder of the same height so the server markup matches.
 */
export function ChartFrame({
  height,
  ariaLabel,
  children,
  className,
}: {
  readonly height: number;
  readonly ariaLabel: string;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}): ReactNode {
  const mounted = useMounted();
  const style: CSSProperties = { height };
  return (
    <div
      className={cx(styles.frame, className)}
      style={style}
      role="img"
      aria-label={ariaLabel}
    >
      {mounted ? children : <div className={styles.placeholder} aria-hidden />}
    </div>
  );
}

/** A small color swatch keying a series in a tooltip row. */
export function TooltipSwatch({ color }: { readonly color: string }): ReactNode {
  return <span className={styles.swatch} style={{ background: color }} aria-hidden />;
}
